import { Instance, flow, getRoot, types } from 'mobx-state-tree';
import { RootStoreModel } from '../Root/RootStore';
import { SET_VISITED_PAGE } from '../../mutations/User';
import { VisitedPages } from '../../../types/types';
import { captureException } from '@sentry/react-native';
import { client } from '../../../config/apollo/client';
import { loadString, saveString } from '../../../utils/storage';

export const OnboardingModel = types
  .model('OnboardingModel', {
    onboardingShow: types.maybeNull(types.boolean),
    firstTime: types.maybeNull(types.boolean),
    dashboardIndex: types.maybeNull(types.number),
    visitedPages: types.optional(types.array(types.string), []),
  })
  .actions(self => {
    const   setVisitedPages =  flow(function* (page: VisitedPages) {
      // Record it LOCALLY FIRST. This flag is what hides the coach mark, and it used to be
      // set only once the mutation had resolved -- so on a slow network it stayed up for the
      // whole round trip, and on a failure (offline, or the resolver filtered out of the
      // schema for this role) it stayed up forever. The overlay is a full-screen Modal that
      // swallows every touch, so that left the whole app unusable. The server call is
      // best-effort: it only has to catch up.
      if(!self.visitedPages) {
        self.visitedPages = [page];
      } else if(!self.visitedPages.includes(page)) {
        self.visitedPages.push(page);
      }
      try {
        yield client.mutate({ mutation: SET_VISITED_PAGE, fetchPolicy: 'no-cache', variables: {page} });
      } catch (error) {
        captureException(error);
        return null;
      }
    });
    const  loadVisitedPages =  async (pages: VisitedPages[])  =>{
      // Merge rather than overwrite. setVisitedPages records the page locally before the
      // server has it, so a profile refetch that was already in flight would otherwise wipe
      // it and re-open the coach mark the user had just dismissed. The server list only ever
      // grows, so nothing is lost by keeping what we already know.
      self.visitedPages = Array.from(new Set([...(self.visitedPages ?? []), ...pages])) as string[];
      if(!!getRoot(self)?.user?.activePath && !pages.includes('DASHBOARD')) {
        const activePathString =  await loadString('activePath');
        if(!activePathString) {
          saveString('activePath','true');
        }
      }
    };
    return {
      set(value: boolean) {
        self.onboardingShow = value;
      },
      notFirstTime () {
        self.firstTime = false;
      },
      init() {
        self.dashboardIndex = 0;
        self.firstTime = true;
      },
      setVisitedPages,
      loadVisitedPages
    };
  })
  .views(self => ({
    isVisited(page: VisitedPages) {
      const stateGetProfile = getRoot<Instance<typeof RootStoreModel>>(self)?.user?.stateGetProfile;
      if(!(stateGetProfile === 'done')) {
        return true;
      }
      if (!self?.visitedPages) {
        return false;
      }else {
        return self.visitedPages.includes(page);
      }
    },

  }
  ));


export type IOnboardingStore = Instance<typeof OnboardingModel>;
